// DST-correct "08:40 UK" marker for NIY intraday bars (Task 11). London
// wall-clock 08:40 falls at 07:40Z during BST (late Mar - late Oct) and at
// 08:40Z during GMT (late Oct - late Mar). We derive the UTC instant via
// Intl.DateTimeFormat against Europe/London rather than hard-coding either
// offset, so the marker stays correct across the DST transition without a
// manual calendar table.

interface LondonDate { year: number; month: number; day: number } // month is 0-indexed (Date.UTC convention)

/** The Europe/London calendar date (y/m/d, 0-indexed month) a UTC instant falls on. */
function londonDateOf(utc: Date): LondonDate {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
  })
  const parts = fmt.formatToParts(utc)
  return {
    year: Number(parts.find(p => p.type === 'year')?.value),
    month: Number(parts.find(p => p.type === 'month')?.value) - 1,
    day: Number(parts.find(p => p.type === 'day')?.value),
  }
}

function sameLondonDate(a: LondonDate, b: LondonDate): boolean {
  return a.year === b.year && a.month === b.month && a.day === b.day
}

/**
 * Given a Europe/London calendar date (y/m/d, 0-indexed month), return the
 * UTC instant corresponding to 08:40 London wall-clock time on that date.
 *
 * London's UTC offset is always 0 (GMT) or +60 minutes (BST), so a UTC
 * "guess" built from the same y/m/d at 08:40 never rolls onto a different
 * UTC calendar date once shifted by the offset -- safe to compute this way.
 */
export function londonMarkerUtcForDate(date: LondonDate): Date {
  const guess = new Date(Date.UTC(date.year, date.month, date.day, 8, 40))
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  })
  const parts = fmt.formatToParts(guess)
  const hh = Number(parts.find(p => p.type === 'hour')?.value ?? '8')
  const mm = Number(parts.find(p => p.type === 'minute')?.value ?? '40')
  const localMinutes = hh * 60 + mm
  const targetMinutes = 8 * 60 + 40
  const offsetMinutes = localMinutes - targetMinutes // BST=+60, GMT=0
  return new Date(guess.getTime() - offsetMinutes * 60_000)
}

/**
 * Given any UTC instant, return the UTC Date corresponding to 08:40 London
 * wall-clock time on the Europe/London calendar date that instant falls on.
 */
export function londonMarkerUtc(referenceUtc: Date): Date {
  return londonMarkerUtcForDate(londonDateOf(referenceUtc))
}

/**
 * Find the index of the bar closest to 08:40 London wall-clock time on the
 * LATEST bar's Europe/London calendar day.
 *
 * The /api/japan/niy-intraday endpoint returns a rolling last-24h window, so
 * it routinely straddles two London calendar days (yesterday's ~08:40 and
 * today's). The target is anchored once, to the latest bar's London day --
 * never computed per-bar, and never derived from the UTC calendar date
 * (which can disagree with the London date late in the London evening, e.g.
 * 23:30Z in BST is already 00:30 London the next day) -- so a bar from a
 * prior day can't win the "nearest" comparison just because it happens to
 * sit close to ITS OWN day's 08:40.
 *
 * If that day's 08:40 London instant is still in the future relative to the
 * most recent bar (i.e. today's decision hasn't printed yet), this returns
 * -1 (no marker) rather than falling back to a bar from a prior day.
 */
export function findMarkerIndex(bars: { tsUtc: string }[]): number {
  if (bars.length === 0) return -1
  const latest = new Date(bars[bars.length - 1].tsUtc)
  const latestLondonDate = londonDateOf(latest)
  const target = londonMarkerUtcForDate(latestLondonDate)
  if (target.getTime() > latest.getTime()) return -1

  let bestIdx = -1
  let bestDiff = Infinity
  for (let i = 0; i < bars.length; i++) {
    const d = new Date(bars[i].tsUtc)
    if (!sameLondonDate(londonDateOf(d), latestLondonDate)) continue // exclude a prior London day's bars
    const diff = Math.abs(d.getTime() - target.getTime())
    if (diff < bestDiff) { bestDiff = diff; bestIdx = i }
  }
  return bestIdx
}
